import { FC, useEffect, useState } from 'react';

interface AudioControlProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

const AudioControl: FC<AudioControlProps> = ({ audioRef }) => {
  const [volume, setVolume] = useState(100);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.muted = muted;
      if (!muted) {
        audioRef.current.volume = volume / 100;
      }
    }
  }, [audioRef.current, volume, muted]);

  // Listen for sync events from PiP window
  useEffect(() => {
    const handleAudioControlSync = (event: Event) => {
      const customEvent = event as CustomEvent<{
        volume: number;
        muted: boolean;
      }>;
      const { volume: syncVolume, muted: syncMuted } = customEvent.detail;
      console.log('🔊 AudioControl received sync event', {
        syncVolume,
        syncMuted,
      });

      // Apply mute state to the audio element
      if (audioRef.current) {
        audioRef.current.muted = syncMuted;
        if (!syncMuted) {
          audioRef.current.volume = syncVolume;
        }
      }

      setVolume(syncMuted ? 0 : syncVolume * 100);
      setMuted(syncMuted);
    };

    window.addEventListener('audioControlSync', handleAudioControlSync);

    return () => {
      window.removeEventListener('audioControlSync', handleAudioControlSync);
    };
  }, [audioRef]);

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const value = parseFloat(e.target.value);

    // Update local state immediately
    setVolume(value * 100);
    setMuted(false);

    // Apply to audio element if available
    if (audioRef.current) {
      audioRef.current.volume = value;
      if (audioRef.current.muted) {
        audioRef.current.muted = false;
      }

      // Notify PiP window about volume change
      console.log('🔊 Main AudioControl volume changed, notifying PiP', {
        value,
      });
    }
  };

  const handleVolumeMouseDown = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleVolumeTouchStart = (e: React.TouchEvent<HTMLInputElement>) => {
    e.stopPropagation();
  };

  const handleMuteToggle = () => {
    // Update local state immediately
    const newMuted = !muted;
    setMuted(newMuted);

    // Apply to audio element if available
    if (audioRef.current) {
      audioRef.current.muted = newMuted;

      // Notify PiP window about mute change
      console.log('🔊 Main AudioControl mute toggled, notifying PiP', {
        muted: newMuted,
        volume: audioRef.current.volume,
      });
    }
  };

  return (
    <div className="relative tc-mv-volume-wrapper hidden md:block">
      <div className="absolute flex justify-center bottom-full left-0 right-0 rounded-full pb-2">
        <div className="w-8 flex items-center justify-center rounded-full tc-mv-volume p-2">
          <input
            type="range"
            className="w-2 rounded-md h-full appearance-none bg-transparent cursor-pointer tc-mv-volume-slider"
            min="0"
            max="1"
            step="0.01"
            value={muted ? 0 : volume / 100}
            onChange={handleVolumeChange}
            onMouseDown={handleVolumeMouseDown}
            onTouchStart={handleVolumeTouchStart}
            style={{
              background: `linear-gradient(to top, white 0%, white ${
                muted ? 0 : volume
              }%, rgba(0,0,0,0.4) ${
                muted ? 0 : volume
              }%, rgba(0,0,0,0.4) 100%)`,
            }}
          />
        </div>
      </div>
      <button
        className="tc-waveform-icon w-[40px] align-middle text-white"
        onClick={handleMuteToggle}>
        {muted || volume === 0 ? (
          <i className="fa-solid fa-volume-xmark" />
        ) : (
          <i className="fa-solid fa-volume-high" />
        )}
      </button>
    </div>
  );
};

export { AudioControl };
